import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, Label, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const ErrorSupportPage = () => {
  const [errorRateData, setErrorRateData] = useState(null);
  const [failedActionsData, setFailedActionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchErrorRate();
    fetchFailedActions();
  }, []);

  const fetchErrorRate = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/error-encounter-rate`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setErrorRateData(response.data);
    } catch (err) {
      console.error('Failed to fetch error rate:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };


  const fetchFailedActions = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/failed-actions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFailedActionsData(response.data);
    } catch (err) {
      console.error('Failed to fetch failed actions:', err);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading error analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Error & Support Tracking</h1>
          <p className="text-purple-200 text-sm">Portal stability and error monitoring</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchErrorRate}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Error & Support Tracking</h1>
              <p className="text-purple-200 text-sm">Portal stability and error monitoring</p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchErrorRate();
              fetchFailedActions();
            }}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Chart 1 (Item 13): Error Encounter Rate */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Error Encounter Rate (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={errorRateData?.daily_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left">
              <Label value="Sessions with Errors" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <YAxis yAxisId="right" orientation="right">
              <Label value="Error Rate (%)" angle={90} position="insideRight" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="sessions_with_errors" stroke="#ef4444" strokeWidth={2} name="Sessions with Errors" />
            <Line yAxisId="right" type="monotone" dataKey="error_rate" stroke="#f97316" strokeWidth={2} name="Error Rate %" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> The number of admin sessions that encountered at least one error (red line) and the error rate percentage (orange line) over the last 30 days.
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> Aim for error rate below 2% (excellent portal stability). Rates above 5% indicate UX problems, validation issues, or backend instability. Spikes suggest new bugs introduced - correlate with deployment dates. Consistent high error rates require urgent attention - review error logs, fix validation rules, or improve error handling. Track which pages/actions generate most errors to prioritize fixes.
          </p>
        </div>
      </div>

      {/* Chart 2 (Item 14): Failed Actions by Type */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Failed Actions by Type (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart 
            data={failedActionsData?.failed_actions || []}
            layout="vertical"
            margin={{ left: 10, right: 20, top: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis 
              dataKey="action" 
              type="category" 
              width={150}
              style={{ fontSize: '12px' }}
            />
            <Tooltip />
            <Legend />
            <Bar dataKey="failures" fill="#ef4444" name="Failed Attempts" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> The top 20 most frequently failing admin actions (create user, create site, approve expense, etc.) ranked by failure count over the last 30 days.
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> High failure rates on specific actions indicate UX problems or overly strict validation. If "create_user" fails frequently, check email validation, required fields, or error messages. If "approve_expense" fails often, investigate approval workflow or permission issues. Use this to prioritize bug fixes and UX improvements. Actions with many failures despite good UX may indicate admin training gaps - create guides or tooltips for those features.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ErrorSupportPage;
